/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, useMemo } from "react"
import { Card, Row, Col, Dropdown } from "@duxton-web/ui"
import { MoreVerticalUniversal } from "@duxton-web/icons"
import { useSelector, useDispatch } from "react-redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { Department, SubDepartment } from "types/models/menu"
import { SortableItemList } from "components/common"
import { TroyWebTracker } from "utils/tracking"
import { debounceSortHandler } from "utils/misc"
import { EventAction, EventCategory } from "constants/tracking"
import { getUCMConfig } from "utils/config"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"

import { sortMenuItemNewLogic } from "utils/sort_helper"
import {
  getItemWithoutPhotoCount,
  isItemWithoutPhotoMode,
} from "utils/menu_helper"
import commonStyles from "styles/common.modules.css"
import { MenuSubDepartmentRow } from "./MenuSubDepartmentRow"

interface IProps {
  hasAction: boolean
}

export const MenuOverviewSubDepartments: FC<IProps> = ({
  hasAction,
}: IProps) => {
  const dispatch = useDispatch()

  const {
    overview: { subDepartments, department: selectedDepartment },
    merchantID,
    isEditAllowed,
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
    isUCS,
    itemFilter,
  } = useSelector((state: AppState) => state?.menu)

  const filteredDepartments = useMemo(() => {
    if (isItemWithoutPhotoMode(itemFilter)) {
      return subDepartments.filter(subDepartment => {
        return getItemWithoutPhotoCount(subDepartment.items) !== 0
      })
    }
    return subDepartments
  }, [itemFilter, subDepartments])

  const selectedSubDepartment = useSelector(
    (state: AppState) => state?.menu?.overview?.subDepartment,
  )

  const handleMenuClick = (key: string) => {
    let VALUE = null

    switch (key) {
      case "rearrange":
        dispatch(
          actionCreator.menu.toggleDrawer({
            showSortSubDepartments: true,
          }),
        )
        VALUE = "REARRANGE"
        break
      case "multiple":
        dispatch(
          actionCreator.menu.toggleDrawer({
            showItemSelection: true,
          }),
        )
        VALUE = "SELECT_MULTIPLE"
        break
      default:
        break
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickSubDepartmentMore,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        NUM_SUB_DEPARTMENT: filteredDepartments?.length ?? 0,
        VALUE,
      },
    })
  }

  const handleSelectSubDepartment = (subDepartment: SubDepartment) => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickSubDepartmentName,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        SUB_DEPARTMENT_ID: subDepartment.itemClassID,
      },
    })

    dispatch(actionCreator.menu.selectSubDepartment(subDepartment))
  }

  const renderRow = (subDepartment: SubDepartment) => {
    return <MenuSubDepartmentRow subDepartment={subDepartment} />
  }

  const textMap = useTranslationsMap({
    rearrange: "menu.actions.rearrange",
    selectMultiple: "menu.actions.selectMultiple",
    sortCategoriesSuccess: "menu.notifications.sortCategories.successMessage",
  })

  const onSortEnd = debounceSortHandler(
    (sortedDepartments: Department[], newIndex: number) => {
      const departmentID = selectedDepartment?.itemClassID ?? ""

      TroyWebTracker.sendEvent({
        category: EventCategory.menuOverviewRearrangeSubDepartment,
        action: EventAction.submitRearrangeSubDepartment,
        labels: {
          ...(merchantID
            ? { STORE_ID: merchantID }
            : {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              }),
          CATEGORY_ID: departmentID,
        },
      })

      const sorts = isUCS
        ? sortMenuItemNewLogic({
            newIndex,
            newList: sortedDepartments,
            sortKey: "sortOrder",
            itemKey: "itemClassID",
          })
        : sortedDepartments.map((department, index) => ({
            resourceID: department.itemClassID ?? "",
            sortOrder: index + 1,
          }))

      dispatch(
        actionCreator.menu.sortDepartments({
          merchantID,
          sorts,
          parentDepartmentID: departmentID,
          successMessage: textMap.sortCategoriesSuccess,
          menuGroupID,
          merchantGroupID,
        }),
      )
    },
  )

  return (
    <Card
      className={`${commonStyles.menuTableCard} ${commonStyles.center}`}
      title={
        <Row justify="space-between">
          <Col>{useTranslation("menu.headings.subCategories")}</Col>
          {hasAction && getUCMConfig("enableTableMenuButton") && (
            <Col>
              <Dropdown
                data-testid="menu-button-dropdown"
                menu={{
                  items: [
                    ...(isEditAllowed &&
                    !(
                      filteredDepartments?.length === 0 ||
                      selectedSubDepartment === null
                    )
                      ? [
                          {
                            key: "rearrange",
                            label: textMap.rearrange,
                            onClick: () => handleMenuClick("rearrange"),
                          },
                        ]
                      : []),
                    {
                      key: "multiple",
                      label: textMap.selectMultiple,
                      onClick: () => handleMenuClick("multiple"),
                    },
                  ],
                }}
                trigger={["click"]}
              >
                <MoreVerticalUniversal />
              </Dropdown>
            </Col>
          )}
        </Row>
      }
    >
      <SortableItemList
        items={filteredDepartments}
        renderRowContent={renderRow}
        useDragHandle
        isItemSelected={(subDepartment: SubDepartment) =>
          subDepartment.itemClassID === selectedSubDepartment?.itemClassID
        }
        onRowClick={(_, subDepartment: SubDepartment) =>
          handleSelectSubDepartment(subDepartment)
        }
        draggable={getUCMConfig("enableDraggableList")}
        onSort={({ newList, newIndex }) => {
          onSortEnd(newList, newIndex)
        }}
      />
    </Card>
  )
}
